import React, { useEffect, useRef } from "react";
import createGlobe from "cobe";

const EU_MARKERS = [
  { location: [48.8566, 2.3522], size: 0.06 }, // Paris
  { location: [52.52, 13.405], size: 0.05 }, // Berlin
  { location: [52.3676, 4.9041], size: 0.05 }, // Amsterdam
  { location: [50.8503, 4.3517], size: 0.045 }, // Brussels
  { location: [41.9028, 12.4964], size: 0.05 }, // Rome
  { location: [40.4168, -3.7038], size: 0.05 }, // Madrid
  { location: [52.2297, 21.0122], size: 0.045 }, // Warsaw
  { location: [45.4642, 9.19], size: 0.04 }, // Milan
  { location: [51.5074, -0.1278], size: 0.05 }, // London
  { location: [59.3293, 18.0686], size: 0.04 }, // Stockholm
  { location: [38.7223, -9.1393], size: 0.04 }, // Lisbon
  { location: [47.3769, 8.5417], size: 0.04 }, // Zurich
  { location: [55.6761, 12.5683], size: 0.04 }, // Copenhagen
  { location: [50.0755, 14.4378], size: 0.04 }, // Prague
  { location: [47.4979, 19.0402], size: 0.04 }, // Budapest
];

const EU_ARCS = [
  { from: [48.8566, 2.3522], to: [52.52, 13.405] },
  { from: [48.8566, 2.3522], to: [41.9028, 12.4964] },
  { from: [48.8566, 2.3522], to: [40.4168, -3.7038] },
  { from: [52.3676, 4.9041], to: [52.2297, 21.0122] },
  { from: [51.5074, -0.1278], to: [50.8503, 4.3517] },
  { from: [50.8503, 4.3517], to: [45.4642, 9.19] },
  { from: [52.52, 13.405], to: [59.3293, 18.0686] },
  { from: [41.9028, 12.4964], to: [47.4979, 19.0402] },
  { from: [40.4168, -3.7038], to: [38.7223, -9.1393] },
  { from: [52.52, 13.405], to: [50.0755, 14.4378] },
  { from: [48.8566, 2.3522], to: [47.3769, 8.5417] },
  { from: [52.3676, 4.9041], to: [55.6761, 12.5683] },
  { from: [51.5074, -0.1278], to: [52.52, 13.405] },
  { from: [45.4642, 9.19], to: [48.8566, 2.3522] },
];

/**
 * Official cobe focus formula (lat/lon → camera angles):
 *   phi   = -(lon ° → rad) - π/2
 *   theta =  (lat ° → rad)   — we keep a milder tilt for look
 */
const lngToPhi = (lngDeg) => -(lngDeg * Math.PI) / 180 - Math.PI / 2;
const EU_PHI = lngToPhi(8); // central Europe (~Germany)
const EU_PHI_WEST = lngToPhi(-12); // Atlantic / Portugal  (larger phi)
const EU_PHI_EAST = lngToPhi(28); // Eastern Europe         (smaller phi)
const EU_THETA = 0.36;

/** Lightweight WebGL globe (cobe) — Europe-focused Mottruck network. */
const CarrierGlobe = ({ size = 360 }) => {
  const canvasRef = useRef(null);
  const pointerInteracting = useRef(null);
  const pointerInteractionMovement = useRef(0);
  const phiRef = useRef(EU_PHI);
  const phiDirRef = useRef(1);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    // Always (re)start facing Europe
    phiRef.current = EU_PHI;
    phiDirRef.current = 1;
    pointerInteractionMovement.current = 0;

    let rafId = 0;
    let arcTimer = 0;
    let arcOffset = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const ARC_VISIBLE = 8;

    const sliceArcs = (offset) => {
      const out = [];
      for (let i = 0; i < ARC_VISIBLE; i += 1) {
        out.push(EU_ARCS[(offset + i) % EU_ARCS.length]);
      }
      return out;
    };

    const globe = createGlobe(canvas, {
      devicePixelRatio: dpr,
      width: size * 2,
      height: size * 2,
      phi: EU_PHI,
      theta: EU_THETA,
      dark: 0,
      diffuse: 1.35,
      mapSamples: 20000,
      mapBrightness: 6,
      baseColor: [1, 1, 1],
      markerColor: [1, 0.8, 0.34],
      glowColor: [0.86, 0.88, 0.95],
      scale: 1.04,
      markers: EU_MARKERS,
      arcs: sliceArcs(0),
      arcColor: [0.1, 0.14, 0.3],
      arcWidth: 0.55,
      arcHeight: 0.18,
      markerElevation: 0.028,
    });

    const tick = () => {
      if (pointerInteracting.current === null) {
        // Slow ping-pong drift across Europe only (west ↔ east)
        phiRef.current += 0.0007 * phiDirRef.current;
        if (phiRef.current >= EU_PHI_WEST) phiDirRef.current = -1;
        if (phiRef.current <= EU_PHI_EAST) phiDirRef.current = 1;
      }
      globe.update({
        phi: phiRef.current + pointerInteractionMovement.current,
        theta: EU_THETA,
        width: size * 2,
        height: size * 2,
      });
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    arcTimer = window.setInterval(() => {
      arcOffset = (arcOffset + 2) % EU_ARCS.length;
      globe.update({ arcs: sliceArcs(arcOffset) });
    }, 2800);

    canvas.style.opacity = "1";

    return () => {
      cancelAnimationFrame(rafId);
      window.clearInterval(arcTimer);
      globe.destroy();
    };
  }, [size]);

  return (
    <div className="carrier-globe" style={{ width: size, height: size }}>
      <canvas
        ref={canvasRef}
        className="carrier-globe-canvas"
        width={size * 2}
        height={size * 2}
        style={{
          width: "100%",
          height: "100%",
          contain: "layout paint size",
          opacity: 0,
          transition: "opacity 0.8s ease",
          cursor: "grab",
        }}
        onPointerDown={(e) => {
          pointerInteracting.current =
            e.clientX - pointerInteractionMovement.current * 120;
          if (canvasRef.current) canvasRef.current.style.cursor = "grabbing";
        }}
        onPointerUp={() => {
          pointerInteracting.current = null;
          if (canvasRef.current) canvasRef.current.style.cursor = "grab";
        }}
        onPointerOut={() => {
          pointerInteracting.current = null;
          if (canvasRef.current) canvasRef.current.style.cursor = "grab";
        }}
        onMouseMove={(e) => {
          if (pointerInteracting.current !== null) {
            pointerInteractionMovement.current =
              (e.clientX - pointerInteracting.current) / 120;
          }
        }}
        onTouchMove={(e) => {
          if (pointerInteracting.current !== null && e.touches[0]) {
            pointerInteractionMovement.current =
              (e.touches[0].clientX - pointerInteracting.current) / 60;
          }
        }}
      />
    </div>
  );
};

export default CarrierGlobe;
